/**
 * Upload Center: everything the queue is carrying, grouped by creative.
 *
 * The dialog that starts an upload is a door, not a container. This page is where
 * the work actually lives, so it survives closing that dialog, walking to another
 * creative and queueing more there.
 */
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { CloudUpload, Check, AlertTriangle, RotateCcw, Trash2, Loader2, Pause, Play, Ban } from 'lucide-react';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { PageHeader } from '@/components/ui/Card';
import { useUploadCenter, formatBytes, formatSpeed, formatEta, type UploadJob } from '@/lib/upload-center';
import { cn } from '@/lib/cn';

const STATE_LABEL: Record<UploadJob['state'], string> = {
  queued: 'waiting', uploading: 'uploading', paused: 'paused', done: 'uploaded', failed: 'failed', cancelled: 'cancelled',
};

/** What the transfer is actually doing, in words the crew use. */
function stageText(j: UploadJob): string {
  if (j.state === 'failed') return j.error ?? 'failed';
  if (j.state === 'done') return 'in Google, recorded';
  if (j.state === 'queued') return 'waiting for a free lane';
  if (j.state === 'cancelled') return 'cancelled';
  if (j.state === 'paused') return `paused${j.loaded ? `, ${formatBytes(j.loaded)} of ${formatBytes(j.size)} already sent` : ''}`;
  switch (j.stage) {
    case 'requesting': return 'opening the upload session';
    case 'thumbnail': return 'making the thumbnail';
    case 'finalizing': return 'recording it on the creative';
    default: {
      const sent = `${formatBytes(j.loaded || j.size * (j.pct / 100))} of ${formatBytes(j.size)}`;
      const rate = formatSpeed(j.bps);
      const left = j.bps && j.bps > 0
        ? formatEta(Math.round(Math.max(0, j.size - j.loaded) / j.bps))
        : '';
      return sent + (rate ? ` at ${rate}` : '') + (left ? `, ${left} left` : '');
    }
  }
}

export default function UploadCenterPage() {
  const up = useUploadCenter();
  const { jobs, active, queued, paused, failed, done, pendingBytes, bytesPerSec, etaSeconds } = up;
  const [confirmCancel, setConfirmCancel] = useState(false);

  // Group by creative, newest group first, so a shoot reads as one block per creative.
  const groups = new Map<string, UploadJob[]>();
  for (const j of jobs) {
    const list = groups.get(j.creativeId);
    if (list) list.push(j); else groups.set(j.creativeId, [j]);
  }
  const ordered = Array.from(groups.entries()).sort((a, b) => {
    const la = Math.max(...a[1].map((j) => j.queuedAt));
    const lb = Math.max(...b[1].map((j) => j.queuedAt));
    return lb - la;
  });

  const speed = formatSpeed(bytesPerSec);
  const eta = formatEta(etaSeconds);
  const subtitle = jobs.length === 0
    ? 'Nothing uploading.'
    : `${active} uploading, ${queued} waiting${paused ? `, ${paused} paused` : ''}, ${done} done${failed ? `, ${failed} failed` : ''}`
      + (pendingBytes > 0 ? ` - ${formatBytes(pendingBytes)} left` : '')
      + (speed ? ` at ${speed}` : '')
      + (eta ? `, about ${eta} to go` : '');

  return (
    <div>
      <PageHeader
        title="Upload Center"
        subtitle={subtitle}
        actions={(
          <div className="flex flex-wrap items-center gap-2">
            {(up.active + up.queued) > 0 && (
              <button onClick={up.pauseAll} data-testid="pause-all" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-bg-card hover:bg-slate-800 text-muted-strong border border-slate-800">
                <Pause size={12} /> Pause all
              </button>
            )}
            {up.paused > 0 && (
              <button onClick={up.resumeAll} data-testid="resume-all" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-bg-card hover:bg-slate-800 text-muted-strong border border-slate-800">
                <Play size={12} /> Resume {up.paused} paused
              </button>
            )}
            {(up.active + up.queued + up.paused) > 0 && (
              <button onClick={() => setConfirmCancel(true)} data-testid="cancel-all" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-bg-card hover:bg-slate-800 text-muted-strong border border-slate-800 hover:text-red-300">
                <Ban size={12} /> Cancel all
              </button>
            )}
            {failed > 0 && (
              <button onClick={up.retryAllFailed}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-bg-card hover:bg-slate-800 text-muted-strong border border-slate-800">
                <RotateCcw size={12} /> Retry {failed} failed
              </button>
            )}
            {jobs.some((j) => j.state === 'done' || j.state === 'failed' || j.state === 'cancelled') && (
              <button onClick={up.clearFinished}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-bg-card hover:bg-slate-800 text-muted-strong border border-slate-800">
                <Trash2 size={12} /> Clear finished
              </button>
            )}
          </div>
        )}
      />

      {confirmCancel && (
        <ConfirmDialog
          open
          tone="danger"
          title="Cancel everything still to go?"
          message="Every file waiting, moving or paused stops, and what was already sent of them is thrown away. Finished files are not touched."
          confirmLabel="Cancel all"
          cancelLabel="Keep going"
          onConfirm={() => { setConfirmCancel(false); up.cancelAll(); }}
          onCancel={() => setConfirmCancel(false)}
        />
      )}
      {jobs.length === 0 && (
        <div className="rounded-xl border border-slate-800 bg-bg-card/40 py-16 text-center text-muted text-sm">
          <CloudUpload size={28} className="mx-auto mb-2 text-muted/50" />
          Nothing in the queue.
        </div>
      )}

      <div className="space-y-4" data-testid="upload-center-groups">
        {ordered.map(([creativeId, list]) => {
          const g = list[0];
          const gActive = list.filter((j) => j.state === 'uploading' || j.state === 'queued' || j.state === 'paused').length;
          const gDone = list.filter((j) => j.state === 'done').length;
          const gFailed = list.filter((j) => j.state === 'failed').length;
          return (
            <div key={creativeId} className="rounded-xl border border-slate-800 bg-bg-card/40 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-slate-800 flex flex-wrap items-center gap-2">
                <Link to={`/creatives/${creativeId}`} className="text-sm text-muted-strong hover:text-accent-400 font-medium">
                  {g.creativeCode || 'Creative'}
                </Link>
                <span className="text-xs text-muted truncate max-w-[40ch]">{g.creativeTitle}</span>
                <span className="ml-auto text-[11px] text-muted">
                  {gDone}/{list.length} done{gActive ? ` - ${gActive} to go` : ''}{gFailed ? ` - ${gFailed} failed` : ''}
                </span>
              </div>
              <div className="divide-y divide-slate-800/70">
                {list.map((j) => <JobRow key={j.id} j={j} on={{
                  retry: () => up.retry(j.id), cancel: () => up.cancel(j.id), pause: () => up.pause(j.id),
                  resume: () => up.resume(j.id), remove: () => up.remove(j.id) }} />)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface RowActions { retry: () => void; cancel: () => void; pause: () => void; resume: () => void; remove: () => void }

function JobRow({ j, on }: { j: UploadJob; on: RowActions }) {
  return (
    <div className="px-4 py-2.5" data-testid="upload-job" data-state={j.state}>
      <div className="flex items-center gap-2">
        {j.state === 'uploading' && <Loader2 size={13} className="text-accent-400 animate-spin shrink-0" />}
        {j.state === 'done' && <Check size={13} className="text-emerald-400 shrink-0" />}
        {j.state === 'failed' && <AlertTriangle size={13} className="text-red-400 shrink-0" />}
        {(j.state === 'queued' || j.state === 'cancelled') && <CloudUpload size={13} className="text-muted shrink-0" />}
        {j.state === 'paused' && <Pause size={13} className="text-amber-300 shrink-0" />}

        <span className="text-xs text-muted-strong truncate">{j.name}</span>
        {j.category && <span className="text-[10px] px-1.5 py-0.5 rounded border border-slate-800 text-muted shrink-0">{j.category}</span>}

        <span className="ml-auto text-[11px] text-muted shrink-0">{STATE_LABEL[j.state]}</span>
        {j.state === 'uploading' && <span className="text-[11px] text-accent-400 w-9 text-right shrink-0">{j.pct}%</span>}

        {j.state === 'paused' && (
          <button onClick={on.resume} title="Carry on from where it stopped" aria-label="Resume" data-testid="upload-resume"
            className="text-muted hover:text-accent-400 shrink-0"><Play size={13} /></button>
        )}
        {(j.state === 'queued' || j.state === 'uploading') && (
          <button onClick={on.pause} title="Pause, keeping what is already sent" aria-label="Pause" data-testid="upload-pause"
            className="text-muted hover:text-accent-400 shrink-0"><Pause size={13} /></button>
        )}
        {(j.state === 'failed' || j.state === 'cancelled') && (
          <button onClick={on.retry} title="Try this file again" aria-label="Retry"
            className="text-muted hover:text-accent-400 shrink-0"><RotateCcw size={12} /></button>
        )}
        {(j.state === 'queued' || j.state === 'uploading' || j.state === 'paused') && (
          <button onClick={on.cancel} title="Cancel, throwing away what was sent" aria-label="Cancel" data-testid="upload-cancel"
            className="text-muted hover:text-red-300 shrink-0"><Ban size={13} /></button>
        )}
        <button onClick={on.remove} title="Delete from this list" aria-label="Delete from the list" data-testid="upload-delete"
          className="text-muted hover:text-red-300 shrink-0"><Trash2 size={12} /></button>
      </div>

      {j.state === 'uploading' && (
        <div className="h-1 rounded bg-slate-800 mt-1.5 overflow-hidden">
          <div className="h-full bg-accent-500 transition-all" style={{ width: `${j.pct}%` }} />
        </div>
      )}
      <div className={cn('text-[11px] mt-1', j.state === 'failed' ? 'text-red-300' : 'text-muted')}>
        {stageText(j)}
      </div>
    </div>
  );
}
